<template>
  <!-- Import of the registry from Excel or CSV: a preview with errors by row, then «Применить». -->
  <div class="ai-import">
    <div class="ai-import__head">
      <button
        type="button"
        class="ai-link-button"
        @click="$emit('cancel')"
      >
        <ai-icon
          name="chevron"
          :size="14"
          class="ai-import__back"
        />
        Реестр проектов
      </button>
      <h4>Импорт реестра</h4>
      <p>
        Первая строка — заголовки: {{ columns }}. Обязателен только «Шифр»; несколько значений в ячейке
        разделяйте «;» или переносом строки. Удобно начать с экспорта текущего реестра.
      </p>
    </div>

    <div class="ai-import__controls">
      <label class="ai-button ai-import__file">
        <ai-icon
          name="upload"
          :size="14"
        />
        {{ file ? 'Другой файл' : 'Выбрать файл' }}
        <input
          ref="input"
          type="file"
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          :disabled="busy"
          @change="pick"
        >
      </label>
      <span
        v-if="file"
        class="ai-import__name"
      >
        {{ file.name }} · {{ size }}
      </span>
    </div>

    <div
      class="ai-segmented ai-import__mode"
      role="radiogroup"
      aria-label="Как применить файл"
    >
      <button
        v-for="item in modes"
        :key="item.key"
        type="button"
        role="radio"
        :aria-checked="mode === item.key ? 'true' : 'false'"
        :class="{ 'is-active': mode === item.key }"
        :disabled="busy"
        @click="setMode(item.key)"
      >
        {{ item.label }}
      </button>
    </div>
    <p class="ai-import__hint">
      {{ mode === 'replace'
        ? 'Проекты, которых нет в файле, будут удалены из реестра.'
        : 'Новые шифры добавляются, существующие проекты обновляются, остальные не меняются.' }}
    </p>

    <p
      v-if="busy && !applying"
      class="ai-import__line is-muted"
    >
      <span class="ai-spinner ai-spinner--small" />
      Читаю файл…
    </p>
    <p
      v-if="error"
      class="ai-import__line is-error"
      role="alert"
    >
      {{ error }}
    </p>

    <template v-if="preview && !applied">
      <ul class="ai-import__summary">
        <li>Строк: <b>{{ summary.rows }}</b></li>
        <li>без ошибок: <b>{{ summary.valid }}</b></li>
        <li>новых: <b>{{ summary.create }}</b></li>
        <li>обновится: <b>{{ summary.update }}</b></li>
        <li :class="{ 'is-error': summary.errors }">
          с ошибками: <b>{{ summary.errors }}</b>
        </li>
      </ul>
      <p
        v-if="mode === 'replace' && summary.remove"
        class="ai-import__line ai-tone is-danger ai-import__warning"
      >
        <ai-icon
          name="alert"
          :size="15"
        />
        <span>Будет удалено проектов, которых нет в файле: {{ summary.remove }}.</span>
      </p>
      <div class="ai-import__table">
        <table>
          <thead>
            <tr>
              <th>Строка</th>
              <th>Шифр</th>
              <th>Название</th>
              <th>Действие</th>
              <th>Ошибки</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in preview.rows"
              :key="row.line"
              :class="{ 'is-error': row.action === 'error' }"
            >
              <td>{{ row.line }}</td>
              <td>{{ row.code || '—' }}</td>
              <td>{{ row.name }}</td>
              <td>{{ actionLabel(row.action) }}</td>
              <td>{{ (row.errors || []).join('; ') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p
        v-if="preview.rows.length < summary.rows"
        class="ai-import__line is-muted"
      >
        Показаны первые {{ preview.rows.length }} строк из {{ summary.rows }}.
      </p>
    </template>

    <p
      v-if="applied"
      class="ai-import__line ai-tone is-success ai-import__warning"
      role="status"
    >
      <ai-icon
        name="check"
        :size="15"
      />
      <span>
        Реестр обновлён: добавлено {{ applied.created || 0 }}, обновлено {{ applied.updated || 0 }},
        удалено {{ applied.deleted || 0 }}.
      </span>
    </p>

    <div class="ai-import__actions">
      <button
        type="button"
        class="ai-button"
        @click="$emit('cancel')"
      >
        {{ applied ? 'К реестру' : 'Отмена' }}
      </button>
      <button
        v-if="!applied"
        type="button"
        class="ai-button"
        :class="confirming ? 'is-danger' : 'is-primary'"
        :disabled="busy || !preview || !summary.valid"
        @click="apply"
      >
        <span
          v-if="applying"
          class="ai-spinner ai-spinner--small"
        />
        {{ confirming ? 'Подтвердить замену' : 'Применить' }}
      </button>
    </div>
  </div>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import { fileSize } from './format';

  // The panel accepts JSON bodies up to 4 MB: 2.5 MB of file is about 3.4 MB of base64.
  const MAX_BYTES = 2.5 * 1024 * 1024;
  const MODES = [
    { key: 'merge', label: 'Добавить и обновить' },
    { key: 'replace', label: 'Заменить реестр целиком' },
  ];
  const ACTIONS = { create: 'новый', update: 'обновление', error: 'ошибка' };
  const COLUMNS = [
    'Шифр',
    'Название',
    'Адрес',
    'Заказчик',
    'Статус',
    'Начало',
    'Окончание',
    'Другие названия и шифры',
    'Рабочие папки',
    'Ответственные',
    'Разрешённые получатели',
    'Примечание',
  ];

  // The file as base64 without the data URL prefix.
  function readBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  export default {
    name: 'RegistryImport',
    components: { AiIcon },
    data: () => ({
      modes: MODES,
      columns: COLUMNS.join(', '),
      mode: 'merge',
      file: null,
      content: '',
      preview: null,
      applied: null,
      busy: false,
      applying: false,
      // Replacing deletes projects: the first click asks to confirm (the button turns into «Подтвердить замену»).
      confirming: false,
      error: '',
    }),
    computed: {
      summary() {
        return (this.preview && this.preview.summary) || {};
      },
      size() {
        return fileSize(this.file.size);
      },
    },
    methods: {
      actionLabel(action) {
        return ACTIONS[action] || action;
      },
      async pick(event) {
        const [file] = event.target.files || [];
        event.target.value = '';
        if (!file) return;
        this.error = '';
        this.preview = null;
        this.applied = null;
        if (!/\.(xlsx|csv)$/i.test(file.name)) {
          this.error = 'Нужен файл Excel (.xlsx) или CSV.';
          return;
        }
        if (file.size > MAX_BYTES) {
          this.error = `Файл больше 2,5 МБ (${fileSize(file.size)}).`;
          return;
        }
        this.file = file;
        try {
          this.content = await readBase64(file);
        } catch (error) {
          this.error = 'Не удалось прочитать файл.';
          return;
        }
        await this.check();
      },
      setMode(mode) {
        if (this.mode === mode) return;
        this.mode = mode;
        // The number of removed projects depends on the mode.
        if (this.content && !this.applied) this.check();
      },
      request(apply) {
        return api.importRegistry({ filename: this.file.name, content: this.content, apply, mode: this.mode });
      },
      async check() {
        this.confirming = false;
        this.busy = true;
        this.error = '';
        try {
          const { data } = await this.request(false);
          this.preview = data;
        } catch (error) {
          this.preview = null;
          this.error = api.errorText(error, 'Не удалось прочитать файл');
        } finally {
          this.busy = false;
        }
      },
      async apply() {
        const { remove } = this.summary;
        if (this.mode === 'replace' && remove && !this.confirming) {
          this.confirming = true;
          return;
        }
        this.confirming = false;
        this.busy = true;
        this.applying = true;
        this.error = '';
        try {
          const { data } = await this.request(true);
          this.preview = data;
          this.applied = data.applied || { created: 0, updated: 0, deleted: 0 };
          this.$emit('done');
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось применить файл');
        } finally {
          this.busy = false;
          this.applying = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-import {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ai-import__head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    h4 {
      margin: 0;
      color: var(--ui-text);
      font-size: 15px;
      font-weight: 500;
    }

    p {
      margin: 0;
      color: var(--ui-text-muted);
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .ai-import__back {
    transform: rotate(180deg);
  }

  .ai-import__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }

  // The native file input is hidden inside the button-styled label.
  .ai-import__file {
    position: relative;
    overflow: hidden;

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
    }

    &:focus-within {
      box-shadow: var(--ui-focus);
    }
  }

  .ai-import__name {
    min-width: 0;
    color: var(--ui-text);
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .ai-import__mode {
    align-self: flex-start;
    max-width: 100%;
    overflow-x: auto;
  }

  .ai-import__hint {
    margin: -4px 0 0;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-import__line {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    color: var(--ui-text);
    font-size: 13px;

    &.is-muted {
      color: var(--ui-text-muted);
    }

    &.is-error {
      color: var(--ui-danger);
    }

    .ai-icon {
      margin-top: 1px;
    }
  }

  .ai-import__warning {
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
  }

  .ai-import__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 0;
    padding: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
    list-style: none;

    b {
      color: var(--ui-text);
      font-weight: 500;
    }

    .is-error b {
      color: var(--ui-danger);
    }
  }

  .ai-import__table {
    max-height: 320px;
    overflow: auto;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-sm);

    table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 12px;
    }

    th,
    td {
      padding: 4px 8px;
      border-bottom: 1px solid var(--ui-border);
      text-align: left;
      vertical-align: top;
      overflow-wrap: break-word;
    }

    // Line, code and action stay on one line; names and errors wrap.
    td:nth-child(-n + 2),
    td:nth-child(4) {
      white-space: nowrap;
    }

    th {
      position: sticky;
      top: 0;
      background: var(--ui-surface-soft);
      color: var(--ui-text-muted);
      font-weight: 500;
      white-space: nowrap;
    }

    tr.is-error td {
      color: var(--ui-danger);
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }
  }

  .ai-import__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
</style>
